/**
 * QR-3: транспорт msg-ручек для участника, вошедшего по приглашению в
 * браузере. Подставляется в [MsgApi] вместо транспорта ЛК владельца.
 *
 * Отличия от ЛК:
 * - авторизация — member-JWT из [memberSession], а не access_token владельца;
 * - refresh-cookie нет: протухший токен продлевается ПО ЖИВОМУ ТОКЕНУ через
 *   `POST /msg/members/me/token` (срок — год, но перевыпуск обязан работать,
 *   иначе однажды чат умрёт без единого следа);
 * - идём через publicAxios (без интерсепторов ЛК): иначе 401 участника увёл
 *   бы страницу на форму логина владельца — чужую и бессмысленную для него.
 */
import { publicAxios, PublicApiError } from './publicApi';

/**
 * @param session сессия участника (см. memberSession)
 * @param onSession колбэк «токен обновился» — вызывающий сохраняет сессию
 * @param onLost колбэк «токен мёртв» — участнику нужен новый вход по ссылке
 */
export function createMemberTransport(session, { onSession, onLost, http = publicAxios } = {}) {
  let current = session;

  // 🔴 Транспорт закрыт (выход из чата / размонтирование страницы) — ревью
  // Codex р.3. Продление живёт СВОЕЙ жизнью: человек нажимает «Выйти», выход
  // чистит хранилище, а секунду спустя прилетает ответ на уже улетевший
  // refresh, зовёт onSession — и свежий токен ложится обратно в хранилище,
  // которое только что вычистили. Выход бы не выходил: перезагрузка страницы
  // вернула бы участника в чат, из которого он вышел.
  let disposed = false;

  const withAuth = (headers) => ({ ...headers, Authorization: `Bearer ${current.token}` });

  // ATT: пятым аргументом — сырые опции axios (`responseType`, ход загрузки).
  // Вложение это БАЙТЫ: без них ответ на скачивание пришёл бы строкой, и
  // расшифровка получила бы искажённый шифротекст.
  // 🔴 headers из opts СЛИВАЮТСЯ с авторизованными, а не заменяют их (ревью
  // ATT С2 р.1, High): спред opts после headers стирал бы Authorization —
  // загрузка файла участником ловила бы 401, а повтор с «новым» токеном,
  // потеряв его тем же движением, звал бы onLost на живой сессии.
  const call = (method, path, body, headers, opts = null) => http
    .request({
      method, url: path, data: body,
      ...(opts || {}),
      headers: { ...withAuth(headers), ...(opts?.headers || {}) },
    })
    .then(({ data }) => data);

  // 🔴 Продление — ОДНО на всех (ревью Codex р.2). Догон, отправка и SSE бьют
  // параллельно; без общего промиса каждый 401 запускал бы своё продление,
  // второе уходило бы уже НЕДЕЙСТВИТЕЛЬНЫМ токеном и давало ложный onLost —
  // то есть «доступ отозван» на ровном месте.
  let inFlight = null;

  const doRefresh = async () => {
    let data;
    try {
      ({ data } = await http.post('msg/members/me/token', {}, {
        headers: { Authorization: `Bearer ${current.token}` },
      }));
    } catch (e) {
      // Продлить не вышло: приглашение отозвано или участник удалён —
      // это терминально, и молчать нельзя, иначе экран будет вечно «связь…».
      // После выхода молчим: «доступ отозван» на закрытом чате — ложь.
      if (!disposed && [401, 403, 404].includes(e?.response?.status)) onLost?.();
      throw new PublicApiError(e?.response?.status ?? 0,
        e?.response?.data?.detail ?? null, 'members/me/token');
    }
    // Вышли, пока ответ был в пути — токен НЕ отдаём наружу (см. disposed).
    if (disposed) throw new PublicApiError(0, null, 'members/me/token');
    current = { ...current, token: data.access_token };
    onSession?.(current);
    // 🔴 onSession может ЗАКРЫТЬ транспорт (ревью Codex р.7): страница узнаёт
    // из него, что слот в хранилище забрал другой участник, и гасит вкладку
    // на месте. Тогда продлённый токен наружу не отдаём — иначе SSE и повтор
    // запроса продолжили бы ходить member-JWT уже отобранной сессии.
    if (disposed) throw new PublicApiError(0, null, 'members/me/token');
    return current.token;
  };

  const refresh = () => {
    if (disposed) {
      return Promise.reject(new PublicApiError(0, null, 'members/me/token'));
    }
    if (!inFlight) {
      inFlight = doRefresh().finally(() => { inFlight = null; });
    }
    return inFlight;
  };

  return {
    request: async (method, path, body, headers, opts = null) => {
      // 🔴 После выхода member-JWT наружу не уходит ВООБЩЕ (ревью Codex р.4):
      // контроллер останавливается асинхронно и успевал бы дослать конверт или
      // курсор доставки уже из закрытого чата.
      if (disposed) throw new PublicApiError(0, null, path);
      try {
        return await call(method, path, body, headers, opts);
      } catch (e) {
        // 🔴 Ровно ОДИН повтор и только на 401: у ретрая по любому коду
        // конверт ушёл бы дважды, а вечный цикл продлений выглядел бы как
        // «висит» вместо честной ошибки.
        if (e?.response?.status !== 401) throw e;
        await refresh();
        // Пока продлевались, чат мог закрыться (выход, замена участника в
        // соседней вкладке) — повтор с новым токеном уже не имеет права уйти.
        if (disposed) throw new PublicApiError(0, null, path);
        try {
          return await call(method, path, body, headers, opts);
        } catch (e2) {
          // 401 СВЕЖИМ токеном — это уже не «протух», а «доступа больше нет»
          // (участника удалили, приглашение отозвали). Без этого сигнала
          // экран остался бы в вечном «подключение…» (ревью Codex).
          // После выхода молчим — как и на отбитом продлении: доложить о
          // потере доступа в закрытый чат значит писать в размонтированную
          // страницу и врать человеку (ревью Codex р.4).
          if (!disposed && e2?.response?.status === 401) onLost?.();
          throw e2;
        }
      }
    },
    /** «Тычок» перед реконнектом SSE: у участника это и есть продление. */
    poke: () => refresh(),
    /** Токен для SSE. После выхода поток не поднимается (ревью Codex р.4). */
    streamToken: () => {
      if (disposed) throw new PublicApiError(0, null, 'msg/stream');
      return current.token;
    },
    /**
     * Закрыть транспорт: продления больше не уходят, а ответ на улетевшее
     * продление не возвращает сессию к жизни (см. disposed). Вызывать ПЕРЕД
     * чисткой хранилища, а не после — иначе окно гонки остаётся открытым.
     */
    dispose: () => {
      disposed = true;
      inFlight = null;
    },
  };
}
